import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Avatar,
  Paper,
  TablePagination,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  FormGroup,
  Checkbox
} from '@mui/material';
import {
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedIcon,
  Block as BlockIcon,
  Star as StarIcon,
  Group as GroupIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import userService from '../services/userService';
import { formatDistanceToNow } from 'date-fns';
import DashboardLayout from '../components/DashboardLayout';

const UserAccounts = () => {
  const { user: currentUser } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState({
    open: false,
    user: null,
    role: '',
    permissions: [],
    isVerified: false
  });
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    user: null
  });

  const availableRoles = ['user', 'admin', 'moderator', 'premium', 'affiliate'];
  const availablePermissions = [
    'read:own',
    'write:own',
    'read:all',
    'write:all',
    'manage:users',
    'manage:content',
    'manage:settings',
    'manage:affiliates',
    'view:analytics'
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const userData = await userService.getAllUsers();
      setUsers(userData);
      setError('');
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditUser = (user) => {
    setEditDialog({
      open: true,
      user,
      role: user.role,
      permissions: user.permissions || [],
      isVerified: user.isVerified
    });
  };

  const handleCloseEditDialog = () => {
    setEditDialog({
      open: false,
      user: null,
      role: '',
      permissions: [],
      isVerified: false
    });
  };

  const handleSaveUser = async () => {
    try {
      const { user, role, permissions, isVerified } = editDialog;
      
      // Update role if changed
      if (role !== user.role) {
        await userService.updateUserRole(user._id, role);
      }
      
      // Update permissions if changed
      if (JSON.stringify(permissions.sort()) !== JSON.stringify((user.permissions || []).sort())) {
        await userService.updateUserPermissions(user._id, permissions);
      }
      
      // Update verification status if changed
      if (isVerified !== user.isVerified) {
        await userService.updateUserStatus(user._id, isVerified);
      }
      
      setSuccess('User updated successfully');
      handleCloseEditDialog();
      fetchUsers();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update user');
      console.error('Error updating user:', err);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteUser = (user) => {
    setDeleteDialog({
      open: true,
      user
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      user: null
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await userService.deleteUser(deleteDialog.user._id);
      setSuccess('User deleted successfully');
      handleCloseDeleteDialog();
      fetchUsers();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete user');
      console.error('Error deleting user:', err);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handlePermissionChange = (permission) => {
    const { permissions } = editDialog;
    const newPermissions = permissions.includes(permission)
      ? permissions.filter(p => p !== permission)
      : [...permissions, permission];
    
    setEditDialog(prev => ({
      ...prev,
      permissions: newPermissions
    }));
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <AdminIcon color="error" />;
      case 'moderator':
        return <SecurityIcon color="warning" />;
      case 'premium':
        return <StarIcon color="primary" />;
      case 'affiliate':
        return <GroupIcon color="info" />;
      default:
        return <PersonIcon color="action" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'moderator':
        return 'warning';
      case 'premium':
        return 'primary';
      case 'affiliate':
        return 'info';
      default:
        return 'default';
    }
  };

  // Paginated users
  const paginatedUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <DashboardLayout title="User Accounts">
        <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="User Accounts">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Manage all user accounts, roles, and permissions
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Users
                </Typography>
                <Typography variant="h4">
                  {users.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Verified Users
                </Typography>
                <Typography variant="h4">
                  {users.filter(u => u.isVerified).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Affiliate Users
                </Typography>
                <Typography variant="h4">
                  {users.filter(u => u.role === 'affiliate').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Premium Users
                </Typography>
                <Typography variant="h4">
                  {users.filter(u => u.role === 'premium').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Users Table */}
                  <Card>
            <CardContent>
              <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Joined</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Last Active</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((user, index) => (
                      <TableRow 
                        key={user._id}
                        sx={{ 
                          '&:nth-of-type(even)': { backgroundColor: 'grey.25' },
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {user.email.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {user.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ID: {user._id.slice(-8)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getRoleIcon(user.role)}
                          <Chip 
                            label={user.role} 
                            color={getRoleColor(user.role)}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {user.isVerified ? (
                            <VerifiedIcon color="success" fontSize="small" />
                          ) : (
                            <BlockIcon color="error" fontSize="small" />
                          )}
                          <Typography variant="body2">
                            {user.isVerified ? 'Verified' : 'Unverified'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="body2">
                          {formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="body2">
                          {user.lastLogin 
                            ? formatDistanceToNow(new Date(user.lastLogin), { addSuffix: true })
                            : 'Never'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(user)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {user._id !== currentUser?._id && (
                          <Tooltip title="Delete User">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteUser(user)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={users.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog 
          open={editDialog.open} 
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Edit User: {editDialog.user?.email}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Role Selection */}
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editDialog.role}
                  onChange={(e) => setEditDialog(prev => ({ ...prev, role: e.target.value }))}
                  label="Role"
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getRoleIcon(role)}
                        <Typography sx={{ textTransform: 'capitalize' }}>
                          {role}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Verification Status */}
              <FormControlLabel
                control={
                  <Switch
                    checked={editDialog.isVerified}
                    onChange={(e) => setEditDialog(prev => ({ ...prev, isVerified: e.target.checked }))}
                  />
                }
                label="User Verified"
              />

              {/* Permissions */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Permissions
                </Typography>
                <FormGroup>
                  <Grid container>
                    {availablePermissions.map((permission) => (
                      <Grid item xs={12} sm={6} key={permission}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={editDialog.permissions.includes(permission)}
                              onChange={() => handlePermissionChange(permission)}
                            />
                          }
                          label={permission}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseEditDialog}
              variant="contained"
              data-cancel="true"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUser}
              variant="contained"
              disabled={!editDialog.user}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
        >
          <DialogTitle>
            Delete User
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete user <strong>{deleteDialog.user?.email}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDeleteDialog}
              variant="contained"
              data-cancel="true"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default UserAccounts; 